/**
 * The sockets of one tab (#1809, WS_DESIGN_1809 W11). Code speaks of Victor's four roles; one table says which socket
 * carries each. The default is two sockets, `market` and `user`: four per tab would stop at two tabs against HL's 10
 * sockets per IP (section 3). Going to four is the one line at SOCKET_OF_ROLE, nothing else changes.
 *
 * - One WsManager per socket, each opened by its first subscribe, so nothing connects until something subscribes: GeoGate
 *   keeps a blocked visitor's app unmounted, so that visitor opens no socket.
 * - A reconnect fires the hooks of its own socket only, so it re-syncs that socket's streams and no others.
 * - All sockets share the tab's ConnectBudget, sized from the table (newSocketBudget, failedSocketBudget), so four sockets
 *   do not start a tab that cannot reopen them.
 * - A socket that carries only the `user` role closes when nothing has held it for IDLE_USER_SOCKET_MS, so no wallet and no
 *   ghost means no user socket. The wait lets a ghost hop keep its socket instead of spending a new one. Other sockets
 *   stay: closing an instrument socket between markets would burn the budget (only reachable at four sockets).
 * - `state` is the worst of the sockets in use. A socket nothing has asked for, the user socket with no wallet, is
 *   `closed` and does not count.
 * - sleep() and wake() (#1809, W12, tabSleep.ts): the tab's sockets close and reopen together. From sleep() until they are all
 *   open again, or for WAKE_QUIET_MS, `state` reads `open`: a tab coming back is not an outage, and this is the state the
 *   dot and health follow. A socket's own state stays true, so the order ticket sees that the user socket is not up.
 */
import type { Subscription, SubscriptionType } from './types';
import { WsManager, type WsCounters, type WsManagerOptions, type WsState } from './ws';

export type WsRole = 'user' | 'outcomeMeta' | 'aggregate' | 'instrument';

export type SocketOfRole = Readonly<Record<WsRole, string>>;

const ROLE_OF_TYPE: Readonly<Record<SubscriptionType, WsRole>> = {
  spotState: 'user',
  clearinghouseState: 'user',
  openOrders: 'user',
  orderUpdates: 'user',
  userFills: 'user',
  userHistoricalOrders: 'user',
  userEvents: 'user',
  outcomeMetaUpdates: 'outcomeMeta',
  allMids: 'aggregate',
  l2Book: 'instrument',
  bbo: 'instrument',
  trades: 'instrument',
  activeAssetCtx: 'instrument',
};

export const TWO_SOCKETS: SocketOfRole = {
  user: 'user',
  outcomeMeta: 'market',
  aggregate: 'market',
  instrument: 'market',
};

export const FOUR_SOCKETS: SocketOfRole = {
  user: 'user',
  outcomeMeta: 'meta',
  aggregate: 'aggregate',
  instrument: 'instrument',
};

/** The table in use. */
export const SOCKET_OF_ROLE: SocketOfRole = TWO_SOCKETS;

/** How long an unheld user socket stays open. Longer than the moment a ghost hop leaves it empty. */
export const IDLE_USER_SOCKET_MS = 5_000;

/** After a return, how long the reopening stays out of sight. Past this a socket still not open is a real outage. */
export const WAKE_QUIET_MS = 5_000;

/** The roles each socket of a table carries. */
export function rolesBySocket(table: SocketOfRole): Record<string, WsRole[]> {
  const out: Record<string, WsRole[]> = {};
  for (const role of Object.keys(table) as WsRole[]) (out[table[role]] ??= []).push(role);
  return out;
}

/**
 * New sockets a tab may start in a minute. HL allows 10 sockets and 30 new ones a minute per IP, and tabs do not know of
 * each other, so the tabs that fit the 10 split the 30, with something left over. Two sockets: 5 tabs, 5 each.
 */
export function newSocketBudget(table: SocketOfRole): number {
  const tabs = Math.floor(10 / Object.keys(rolesBySocket(table)).length);
  return Math.floor(29 / tabs);
}

/** Attempts that never opened a tab may make in a minute: six tries for each socket, however many there are. */
export function failedSocketBudget(table: SocketOfRole): number {
  return 6 * Object.keys(rolesBySocket(table)).length;
}

/** The worst first: what the user is told, and what health follows. */
const WORST_FIRST: readonly WsState[] = ['reconnecting', 'connecting', 'open'];

export function combinedWsState(states: readonly WsState[]): WsState {
  return WORST_FIRST.find((s) => states.includes(s)) ?? 'closed';
}

export interface WsSocketsOptions extends WsManagerOptions {
  /** Test seam. */
  socketOfRole?: SocketOfRole;
}

export class WsSockets {
  /** Every socket of the table; a socket nothing has subscribed on is not open. */
  readonly all: readonly WsManager[];
  private readonly byRole = new Map<WsRole, WsManager>();
  private readonly stateListeners = new Set<(s: WsState) => void>();
  private readonly wakeHooks = new Set<(hiddenSince: number) => void>();
  private readonly counters?: WsCounters;
  /** Asleep, or reopening. */
  private quiet = false;
  private quietTimer: ReturnType<typeof setTimeout> | null = null;
  private wokeAt = 0;
  private lastState: WsState = 'closed';

  constructor(url: string, options: WsSocketsOptions = {}) {
    const { socketOfRole = SOCKET_OF_ROLE, ...manager } = options;
    this.counters = manager.counters;
    const managers: WsManager[] = [];
    for (const roles of Object.values(rolesBySocket(socketOfRole))) {
      const onlyUser = roles.every((r) => r === 'user');
      const ws = new WsManager(url, { ...manager, idleCloseMs: onlyUser ? IDLE_USER_SOCKET_MS : undefined });
      managers.push(ws);
      for (const role of roles) this.byRole.set(role, ws);
    }
    this.all = managers;
    for (const ws of managers) ws.onStateChange(() => this.changed());
  }

  forRole(role: WsRole): WsManager {
    return this.byRole.get(role)!;
  }

  /** The socket that carries a stream. */
  forSub(sub: Subscription): WsManager {
    return this.forRole(ROLE_OF_TYPE[sub.type]);
  }

  private get realState(): WsState {
    return combinedWsState(this.all.map((ws) => ws.state));
  }

  get state(): WsState {
    return this.quiet ? 'open' : this.realState;
  }

  private changed(): void {
    if (this.quietTimer && this.realState === 'open') {
      if (this.counters) this.counters.lastReopenMs = Date.now() - this.wokeAt;
      this.endQuiet();
    }
    const now = this.state;
    if (now === this.lastState) return;
    this.lastState = now;
    for (const cb of [...this.stateListeners]) cb(now);
  }

  private endQuiet(): void {
    if (this.quietTimer) clearTimeout(this.quietTimer);
    this.quietTimer = null;
    this.quiet = false;
  }

  /** Called when the combined state changes. */
  onStateChange(cb: (s: WsState) => void): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }

  /** `cb` runs on every wake, with the time the tab was hidden. For what no reconnect hook covers. */
  onWake(cb: (hiddenSince: number) => void): () => void {
    this.wakeHooks.add(cb);
    return () => this.wakeHooks.delete(cb);
  }

  /** Close every socket, keeping what they hold. */
  sleep(): void {
    this.endQuiet();
    this.quiet = true;
    for (const ws of this.all) ws.suspend();
    this.changed();
  }

  /** Reopen. The sockets' reconnect hooks do the re-sync as each opens. */
  wake(hiddenSince: number): void {
    this.wokeAt = Date.now();
    this.quietTimer = setTimeout(() => {
      this.endQuiet();
      this.changed();
    }, WAKE_QUIET_MS);
    for (const ws of this.all) ws.resume();
    for (const cb of [...this.wakeHooks]) cb(hiddenSince);
  }

  /** Env switch or unmount. The instance stays reusable, like a WsManager. */
  close(): void {
    this.endQuiet();
    for (const ws of this.all) ws.close();
  }
}
